import React, { useEffect } from 'react';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import SecondaryBanner from '@ajat/design-system/ContentBlocks/SecondaryBanner';
import ProductsGrid from '@ajat/design-system/ContentBlocks/ProductsGrid';
import Preloader from '@ajat/design-system/Preloader';
import { usePageLoader } from '@ajat/design-system/PageLoader';
import ShopEmptyState from '../SchoolShop/ShopEmptyState';
import ClosedShopMessage from '../ClosedShop/ClosedShopMessage';
import useMenuItemListing from '../../../../hooks/useMenuItemListing';
import useBrandSettings from '../../../../hooks/useBrandSettings';
import useShopCardPrices from '../../../../hooks/useShopCardPrices';
import useSchools from '../../../../hooks/useSchools';
import { STORAGE_KEYS } from '../../../../hooks/useMenuItemNavigation';
import { readFreshSelection } from '../../../../services/schoolSelectionTtl';
import { shopEntry, schoolShopPath } from '../../../../services/menuDestination';
import buildGroupedSections, { readOddCard } from '../SchoolShop/buildGroupedSections';
import resolveHeaderImages from '../SchoolShop/headerImages';
import { applyShopPrices, matchVariation } from '../../../../services/shopCardPrices';
import productShortDescription from '../../../../utils/shortDescription';
import '../SchoolShop/SchoolShop.css';
import { discountCardFields } from '../../../../services/discountBadge';
import { publicHref } from '../../../../config/deployment';

/**
 * MenuItemShop — the no-school fallback listing page for one of the brand's
 * two fixed header menu items (route /clothing/{feSlug}/shop/:menuSlug, where
 * menuSlug is the ASCII-folded menu-item label, e.g. "vogntrojer").
 *
 * Renders the menu item's default collection (server-side fallback: the brand
 * default collection) as a shop-like grid WITHOUT any school context: no
 * selector gating, no localStorage/cart writes. Product cards open the bare
 * PDP, package cards the package configurator — both work school-less.
 *
 * OPEN SHOP PAGE: when the brand's open shop has a group linked to the menu item
 * (admin: Brands → Open shop) the listing carries `group` + `collections[]`, and the
 * page becomes that menu item's public shop page — the grouped school-shop look: photo
 * banner, one titled section per collection, packages at the bottom. It is the page a
 * campaign links to. A visitor who already has a school is sent on to their own school
 * shop (their products, their prices); the cards open the product page, where a brand
 * with a browse-only open shop (CLS-DK) asks for school and class first.
 *
 * Shared across brands; each brand mounts it with its own Header/Footer and
 * copy (see components/Clothing/{BRAND}/MenuItemShop).
 */
const MenuItemShop = ({
  brandSlug,
  feSlug,
  dataBrand,
  HeaderComponent,
  FooterComponent,
  packageDescription = '',
  loadingText = 'Loading…',
  emptyTitle = 'No products right now',
  emptyText = 'No products available right now.',
  contactLabel = 'Contact us',
  faqLabel = 'See FAQ',
  // Product cards append ?variation={collectionItemId} so the bare PDP shows
  // THIS collection item (its color subset, tier price, descriptions) instead
  // of the raw product.
  variationLinks = true,
  // true (CLS-DK): a visitor who has chosen a school sees THAT shop's prices (class and
  // shop overrides) and only what it sells, and a card opens the shop's own variation.
  schoolAware = false,
  // Open shop page (grouped): copy of the packages block + static banner photos per
  // menu key ({menu_2: {backgroundImage, backgroundImageMobile}}) used when the brand
  // uploaded none.
  packagesCaption,
  packagesTitle,
  moreLabel,
  bannerFallback = null,
  // Listing setting «where the large card goes when the product count is odd» (phones,
  // 2-column grid; DS ProductsGrid `oddCard`): 'first' = the lead product on top, the
  // pairs under it; 'last' = the DS default. The payload's `collections[].oddCard`
  // wins, then `group.oddCard`, then this brand default (CLS-DK: 'first', 2026-11-02).
  oddCard,
}) => {
  const navigate = useNavigate();
  const { menuSlug } = useParams();
  const { openShopEnabled, loading: settingsLoading } = useBrandSettings(brandSlug);

  // The BE resolves the slug → menu key and 404s an unknown slug (also covers
  // the dropped legacy menu_1/menu_2 URLs), surfaced here as `error`.
  // With the open shop disabled the BE returns 200 {closed: true, menuItem}
  // and empty lists; the brand flag below is the render authority.
  const { menuItem, collection: collectionMeta, products, packages, group, collections, menuItemImages, loading, error } = useMenuItemListing(brandSlug, menuSlug);
  // A visitor with a stored school on an OPEN SHOP page goes to their own school shop:
  // the sub-shop mapped to this menu item, else the school's stored / first open shop.
  const { schools, loading: schoolsLoading } = useSchools(brandSlug);
  const stored = readFreshSelection(STORAGE_KEYS[brandSlug]);
  const storedSchool = stored?.school ?? null;
  const liveSchool = storedSchool
    ? (schools || []).find((s) => (storedSchool.slug && s.slug === storedSchool.slug) || String(s.id) === String(storedSchool.id)) ?? null
    : null;
  const ownShop = group && liveSchool ? shopEntry(liveSchool, menuItem?.key ?? null, stored?.season ?? null) : { shop: null, group: null };
  const shopPrices = useShopCardPrices(schoolAware ? brandSlug : null);
  const inShop = schoolAware && shopPrices.applied;
  const shopBase = inShop ? `/clothing/${feSlug}/school-shop/${encodeURIComponent(shopPrices.school)}` : null;
  const shopQuery = shopPrices.shop ? `?shop=${encodeURIComponent(shopPrices.shop)}` : '';
  // ABC-SE/CLS-SE/CLS-FI have no Trustpilot profile of their own — the DS default is CLS Denmark's.
  const hideTrustpilot = brandSlug === 'abcse' || brandSlug === 'clsse' || brandSlug === 'clsfi';
  // First load: brand loader over the whole page until the listing (and, for a
  // visitor with a stored school, the schools list behind the redirect) is in.
  usePageLoader(loading || settingsLoading || (Boolean(storedSchool) && schoolsLoading), { brand: dataBrand, label: loadingText });

  /* Set global brand on mount, restore previous on unmount */
  useEffect(() => {
    const prev = document.documentElement.getAttribute('data-brand');
    document.documentElement.setAttribute('data-brand', dataBrand);
    return () => {
      if (prev) document.documentElement.setAttribute('data-brand', prev);
      else document.documentElement.removeAttribute('data-brand');
    };
  }, [dataBrand]);

  // Unknown slug (404) → brand landing. A valid slug with an empty collection
  // returns 200 (no error) and shows the empty state instead.
  if (!loading && error) {
    return <Navigate to={`/clothing/${feSlug}`} replace />;
  }

  if (ownShop.shop) {
    return <Navigate to={schoolShopPath(feSlug, liveSchool.slug, ownShop.shop, ownShop.group)} replace />;
  }

  // "Disable Open Shop": the school-less listing is closed — show the
  // localized closed message instead of the grids (bookmarks land here too).
  if (!settingsLoading && !openShopEnabled) {
    return (
      <div className="school-shop">
        <HeaderComponent />
        <SecondaryBanner tag="" title={menuItem?.label || ''} trustpilot={hideTrustpilot ? null : undefined} />
        <main className="school-shop__wrapper">
          <ClosedShopMessage brandSlug={brandSlug} feSlug={feSlug} />
        </main>
        <FooterComponent />
      </div>
    );
  }

  // A card's page: the visitor's school-shop item when they are in a shop that sells it,
  // else the open shop's product page. It is both the link (crawlers, Ctrl/Cmd clicks)
  // and where a plain click navigates.
  const productPath = (p) => {
    const variation = inShop ? matchVariation(shopPrices.variations, { id: p.id, productId: p.productId, name: p.name }) : null;
    return variation
      ? `${shopBase}/product/${encodeURIComponent(variation.id)}${shopQuery}`
      : `/clothing/${feSlug}/product-details/${encodeURIComponent(p.slug)}`
        + (variationLinks && p.id ? `?variation=${encodeURIComponent(p.id)}` : '');
  };
  const packagePath = (p) => (inShop
    ? `${shopBase}/product-package/${encodeURIComponent(p.slug)}${shopQuery}`
    : `/clothing/${feSlug}/product-package/${encodeURIComponent(p.slug)}`);

  const allProductItems = products
    .filter((p) => p.slug)
    .map((p) => ({
      id: p.id,
      image: p.image || undefined,
      imageAlt: p.name,
      title: p.name,
      description: productShortDescription(p),
      price: p.price,
      ...discountCardFields(p, feSlug),
      colors: Array.isArray(p.colors) ? p.colors : [],
      match: { type: 'product', id: p.id, productId: p.productId ?? null, slug: p.slug, name: p.name },
      href: publicHref(productPath(p)),
      onClick: () => navigate(productPath(p)),
    }));

  const allPackageItems = packages
    .filter((p) => p.slug)
    .map((p) => ({
      id: `pkg-${p.id}`,
      image: p.image || undefined,
      imageAlt: p.name,
      title: p.name,
      description: productShortDescription(p) ?? packageDescription ?? undefined,
      price: p.price,
      match: { type: 'package', id: p.id, slug: p.slug, name: p.name },
      href: publicHref(packagePath(p)),
      onClick: () => navigate(packagePath(p)),
    }));

  // The visitor's shop prices; what the shop does not sell is left out.
  const productItems = inShop ? applyShopPrices(allProductItems, shopPrices, { feSlug }) : allProductItems;
  const packageItems = inShop ? applyShopPrices(allPackageItems, shopPrices, { feSlug }) : allPackageItems;

  const isEmpty = productItems.length === 0 && packageItems.length === 0;

  // Open shop page: one section per collection of the group (kicker + title), unless the
  // group is set to the simple layout. Without a group: the flat grids, as before.
  const sections = group && group.layout !== 'simple'
    ? buildGroupedSections(collections, Object.fromEntries(productItems.map((c) => [c.id, c])), { oddCard: group?.oddCard ?? oddCard })
    : [];
  const flatOddCard = readOddCard(collectionMeta?.oddCard) ?? readOddCard(oddCard);
  const grouped = sections.length > 0;
  const bannerImages = group
    ? resolveHeaderImages({ menuItemImages, menuItemKey: menuItem?.key, fallback: bannerFallback })
    : {};
  // Don't flash the open shop page at a visitor who is about to be sent to their school shop.
  const waitingForSchool = Boolean(storedSchool) && schoolsLoading;

  return (
    <div className="school-shop">
      <HeaderComponent />

      <SecondaryBanner tag={group ? (group.name || menuItem?.label || '') : ''} title={group ? '' : (menuItem?.label || '')} {...bannerImages} trustpilot={hideTrustpilot ? null : undefined} />

      <main className="school-shop__wrapper">
        {(loading || waitingForSchool || isEmpty) ? (
          (loading || waitingForSchool) ? (
            <div style={{ padding: '4rem 2rem', textAlign: 'center' }}>
              <Preloader label={loadingText} />
            </div>
          ) : (
            <ShopEmptyState
              feSlug={feSlug}
              title={emptyTitle}
              text={emptyText}
              contactLabel={contactLabel}
              faqLabel={faqLabel}
            />
          )
        ) : (
          <>
            {grouped && sections.map((section) => (
              <ProductsGrid
                key={section.id}
                caption={section.caption}
                captionVariant={section.captionVariant}
                title={section.title}
                align="left"
                moreLabel={moreLabel}
                paddingTop="large-s"
                paddingBottom="large-s"
                background="var(--color-gray-0)"
                className="school-shop__group"
                items={section.items}
                {...(section.oddCard ? { oddCard: section.oddCard } : {})}
              />
            ))}
            {grouped && packageItems.length > 0 && (
              <ProductsGrid
                caption={packagesCaption}
                captionVariant="success"
                title={packagesTitle}
                align="left"
                className="school-shop__group"
                moreLabel={moreLabel}
                background="var(--color-gray-0)"
                paddingTop="medium-m"
                paddingBottom="large-l"
                items={packageItems}
              />
            )}
            {!grouped && productItems.length > 0 && (
              <ProductsGrid
                paddingTop="medium-l"
                paddingBottom="medium-s"
                background="var(--color-gray-0)"
                items={productItems}
                centerIncomplete
                {...(flatOddCard ? { oddCard: flatOddCard } : {})}
              />
            )}
            {!grouped && packageItems.length > 0 && (
              <ProductsGrid
                background="var(--color-gray-0)"
                paddingTop="medium-m"
                paddingBottom="large-l"
                items={packageItems}
                centerIncomplete
              />
            )}
          </>
        )}
      </main>

      <FooterComponent />
    </div>
  );
};

export default MenuItemShop;
